/**
 * Product Groups - Phone Model Switcher
 * Replacement for Swatch King Product Groups feature
 *
 * This script:
 * 1. Fetches the product groups config from a JSON asset file
 * 2. Finds the current product's group
 * 3. Fetches availability for all products in the group
 * 4. Renders a dropdown to switch between products
 */

(function() {
  'use strict';

  class ProductGroupsManager {
    constructor() {
      this.config = null;
      this.container = null;
      this.currentHandle = null;
      this.currentGroup = null;
      this.availability = {};

      this.init();
    }

    init() {
      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', () => this.setup());
      } else {
        this.setup();
      }
    }

    async setup() {
      // Find the container
      this.container = document.querySelector('product-groups');
      if (!this.container) return;

      this.currentHandle = this.container.dataset.handle;
      if (!this.currentHandle) return;

      // Load config
      await this.loadConfig();
      if (!this.config) return;

      // Find current group
      this.findCurrentGroup();
      if (!this.currentGroup) return;

      // Fetch availability and render
      await this.fetchAvailabilityAndRender();
    }

    async loadConfig() {
      const configEl = document.getElementById('product-groups-config');
      if (!configEl) return;

      try {
        const configData = JSON.parse(configEl.textContent);

        // Fetch the JSON file from the asset URL
        if (configData.jsonUrl) {
          const response = await fetch(configData.jsonUrl);
          if (response.ok) {
            this.config = await response.json();
          }
        }
      } catch (e) {
        console.error('[ProductGroups] Failed to load config:', e);
      }
    }

    findCurrentGroup() {
      const groupIndex = this.config.handles[this.currentHandle];
      if (groupIndex === undefined) return;

      this.currentGroup = this.config.groups[groupIndex];
    }

    async fetchAvailabilityAndRender() {
      const products = this.currentGroup.products;

      // Fetch availability for all products in parallel
      const fetchPromises = products.map(async (product) => {
        try {
          const response = await fetch(`/products/${product.handle}.json`);
          if (response.ok) {
            const data = await response.json();
            this.availability[product.handle] = data.product.available;
          } else {
            // Product might not exist or be draft
            this.availability[product.handle] = false;
          }
        } catch (e) {
          this.availability[product.handle] = false;
        }
      });

      await Promise.all(fetchPromises);
      this.render();
    }

    render() {
      // Filter to only available products
      const availableProducts = this.currentGroup.products.filter(
        p => this.availability[p.handle] !== false
      );

      // If 1 or fewer products available, hide the dropdown entirely
      if (availableProducts.length <= 1) {
        this.container.style.display = 'none';
        return;
      }

      // Find current product data
      const currentProduct = this.currentGroup.products.find(
        p => p.handle === this.currentHandle
      );

      if (!currentProduct) return;

      // Update labels
      const labelEl = this.container.querySelector('.product-groups__label');
      const selectedValueEl = this.container.querySelector('.product-groups__selected-value');
      const buttonTextEl = this.container.querySelector('.product-groups__button-text');
      const popoverTitleEl = this.container.querySelector('.product-groups__popover-title');

      if (labelEl) labelEl.textContent = this.currentGroup.option + ':';
      if (selectedValueEl) selectedValueEl.textContent = currentProduct.value;
      if (buttonTextEl) buttonTextEl.textContent = currentProduct.value;
      if (popoverTitleEl) popoverTitleEl.textContent = this.currentGroup.option;

      // Render options
      const optionsContainer = this.container.querySelector('.product-groups__options');
      if (!optionsContainer) return;

      optionsContainer.innerHTML = '';

      availableProducts.forEach(product => {
        const isSelected = product.handle === this.currentHandle;

        const option = document.createElement('a');
        option.href = `/products/${product.handle}`;
        option.className = 'popover-listbox__option';
        option.textContent = product.value;
        option.setAttribute('role', 'option');
        option.setAttribute('aria-selected', isSelected ? 'true' : 'false');

        if (isSelected) {
          option.classList.add('is-selected');
        }

        optionsContainer.appendChild(option);
      });

      // Show the container
      this.container.style.display = '';

      // Setup popover behavior
      this.setupPopover();
    }

    setupPopover() {
      const trigger = this.container.querySelector('.product-groups__trigger');
      const popover = this.container.querySelector('.product-groups__popover');

      if (!trigger || !popover) return;

      // Set up aria controls
      trigger.setAttribute('aria-controls', popover.id);

      // Click handler for trigger
      trigger.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();

        const isExpanded = trigger.getAttribute('aria-expanded') === 'true';

        if (isExpanded) {
          this.closePopover(trigger, popover);
        } else {
          this.openPopover(trigger, popover);
        }
      });

      // Close on outside click
      document.addEventListener('click', (e) => {
        if (!this.container.contains(e.target)) {
          this.closePopover(trigger, popover);
        }
      });

      // Close on escape
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') {
          this.closePopover(trigger, popover);
        }
      });

      // Close when an option is selected (navigation will happen)
      const options = popover.querySelectorAll('.popover-listbox__option');
      options.forEach(option => {
        option.addEventListener('click', () => {
          this.closePopover(trigger, popover);
        });
      });
    }

    openPopover(trigger, popover) {
      trigger.setAttribute('aria-expanded', 'true');
      popover.hidden = false;

      // If using x-popover custom element, trigger its open method
      if (typeof popover.show === 'function') {
        popover.show();
      }
    }

    closePopover(trigger, popover) {
      trigger.setAttribute('aria-expanded', 'false');
      popover.hidden = true;

      // If using x-popover custom element, trigger its close method
      if (typeof popover.hide === 'function') {
        popover.hide();
      }
    }
  }

  // Initialize
  window.ProductGroupsManager = new ProductGroupsManager();
})();
